import { EventEmitter, Injectable, inject } from "@angular/core";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Layer from "@arcgis/core/layers/Layer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import Sublayer from "@arcgis/core/layers/support/Sublayer";
import WMSSublayer from "@arcgis/core/layers/support/WMSSublayer";
import MapView from "@arcgis/core/views/MapView";
import LayerView from "@arcgis/core/views/layers/LayerView";
import { Observable, Subject, forkJoin, of } from "rxjs";
import {
    concatMap,
    map,
    skipUntil,
    takeUntil,
    tap
} from "rxjs/operators";
import { LayerType } from "~app/common/components/map/map.models";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { LegendGraphicService } from "../layer-legend/service/legend-graphic.service";
import { RespLayerType } from "../models/layer-list.models";
import { INode, Node } from "../models/layer-node.model";
import { RootNode } from "../models/layer-root-node.model";
import { LayerVisibility } from "../models/layer-visibility.model";
import { IDefault, IDefaults } from "../models/perisist-default.model";
import { LayerNodeTreeService } from "./layer-node-tree.service";
import { LayerService } from "./layer-service";

@Injectable({
    providedIn: "root"
})

export class LayerNodeFactory {

    private readonly layerNodeTreeService = inject(LayerNodeTreeService);
    private readonly layerService = inject(LayerService);
    private readonly legendService = inject(LegendGraphicService);

    private unsubscribe$: Subject<void>;
    private nodeTreeCreated$: Subject<void>;

    createNodeTree(nodeConfigs: any[], view: MapView, visibilityEvent: EventEmitter<LayerVisibility>, parent?: INode, defaults?: any): Observable<INode[]> {
        // eslint-disable-next-line sonarjs/deprecation
        if (this.unsubscribe$ && !this.unsubscribe$.isStopped) {
            this.unsubscribe$.next();
            this.unsubscribe$.complete();
        }
        this.unsubscribe$ = new Subject<void>();

        // eslint-disable-next-line sonarjs/deprecation
        if (this.nodeTreeCreated$ && !this.nodeTreeCreated$.isStopped) {
            this.nodeTreeCreated$.next();
            this.nodeTreeCreated$.complete();
        }
        this.nodeTreeCreated$ = new Subject<void>();
        return this.createNodeTreeInternal(nodeConfigs, view, visibilityEvent, parent, defaults)
            .pipe(takeUntil(this.unsubscribe$))
            .pipe(tap(x => {
                this.nodeTreeCreated$.next();
                this.nodeTreeCreated$.complete();
            }));
    }

    private createNodeTreeInternal(nodeConfigs: any[], view: MapView, visibilityEvent: EventEmitter<LayerVisibility>, parent?: INode, defaults?: any): Observable<INode[]> {
        return forkJoin(nodeConfigs.map(a => this.createNode(view, a, visibilityEvent, parent, defaults)));
    }

    private createNode(view: MapView, nodeConfig: any, visibilityEvent: EventEmitter<LayerVisibility>, parent?: INode, defaults?: IDefaults): Observable<INode> {
        if (nodeConfig.hideInLayerList) {
            return of(undefined);
        }

        const type = nodeConfig.type ?? "feature";
        const nd = parent ? new Node(parent, type) : new RootNode(type);
        const defaultVisible = defaults?.defaults?.find(x =>
            ObjectUtils.hasValue(nodeConfig.layerid) &&
            x.layerid === nodeConfig.layerid &&
            x.sublayerid === nodeConfig.sublayerid)?.visible
            ?? false;

        nd.id = nodeConfig.id;
        nd.layerid = nodeConfig.layerid;
        nd.sublayerid = nodeConfig.sublayerid;
        nd.sublayerName = nodeConfig.sublayerName;
        nd.name = nodeConfig.name;
        nd.checked = defaultVisible;
        nd.customLegend = nodeConfig.customLegend;
        nd.partiallyChecked = nodeConfig.partiallyChecked;
        nd.disabled = nodeConfig.disabled;
        nd.visible = defaultVisible;
        nd.minScale = nodeConfig.minScale;
        if (nodeConfig.nodes?.length > 0) {
            return this.createNodeTreeInternal(nodeConfig.nodes, view, visibilityEvent, nd, defaults)
                .pipe(concatMap(x => {
                    nd.nodes = x.filter(y => ObjectUtils.hasValue(y));
                    return this.whenChildNodesAreInitialized(nd, nodeConfig, view, visibilityEvent, defaults);
                }))
                .pipe(map(node => node));
        } else {
            return this.whenChildNodesAreInitialized(nd, nodeConfig, view, visibilityEvent, defaults);
        }
    }

    private whenChildNodesAreInitialized(nd: INode, nodeConfig: any, view: MapView, visibilityEvent: EventEmitter<LayerVisibility>, defaults?: IDefaults): Observable<INode> {
        if (nodeConfig.layerid) {
            return new Observable(subscriber => {
                this.layerService.getViewLayer(nodeConfig.layerid, view, this.nodeTreeCreated$)
                    .pipe(takeUntil(this.nodeTreeCreated$))
                    .subscribe(lw => {
                        this.handleLayerUpdate(lw, nd, view, visibilityEvent, defaults);
                        if (lw && nd.ready) {
                            this.layerNodeTreeService.setCheckedOrPartiallyChecked(nd, nd.checked);
                            this.layerNodeTreeService.copyOpacityToChildren(nd);
                        }
                        subscriber.next(nd);
                        subscriber.complete();
                    });
            });
        } else {
            this.setupNodeEvents(nd, view);
            nd.ready = true;
            this.layerNodeTreeService.setCheckedOrPartiallyChecked(nd, nd.checked);
            this.layerNodeTreeService.copyOpacityToChildren(nd);
            return of(nd);
        }
    }

    private handleLayerUpdate(layerView: LayerView,
        node: INode,
        view: MapView,
        visibilityEvent: EventEmitter<LayerVisibility>,
        defaults?: IDefaults): void {
        if (!layerView) {
            node.ready = layerView === undefined;
            node.error = layerView === undefined;
            return;
        }

        if (node.ready) {
            return;
        }

        this.setupLayerBinding(layerView, node, visibilityEvent, defaults);
        this.setupNodeEvents(node, view);
        node.ready = true;
    }

    private setupLayerBinding(layerView: LayerView, node: INode, visibilityEvent: EventEmitter<LayerVisibility>, defaults?: IDefaults): void {
        const linkedLayer = this.getLinkedLayer(layerView, node);
        const readonly = this.isLayerReadOnly(layerView, linkedLayer);
        if (linkedLayer === undefined) {
            console.warn(`Layer does not exist in map: ${node.layerid}:${node.sublayerid}`);
            node.disabled = true;
            return;
        }
        node.disabled = false;
        this.bindLayerToNode(linkedLayer, layerView.layer, node, readonly, defaults, visibilityEvent);
        if (layerView.layer.type === LayerType.mapImage) {
            node.legends = this.legendService.getLayerLegends(node.layerid, node.sublayerid);
        }
        if (layerView.layer.type === LayerType.wms) {
            const wmsLayer = layerView.layer as WMSLayer;
            node.legends = this.legendService.getWmsLayerLegends(wmsLayer, node.sublayerName);
        }
    }

    private bindLayerToNode(
        layer: RespLayerType,
        featureLayer: Layer,
        node: INode,
        readonly: boolean,
        defaults: IDefaults,
        visibilityEvent: EventEmitter<LayerVisibility>): void {
        const rootNode = this.layerNodeTreeService.getRoot(node);

        const defaultItem = defaults?.defaults?.find(x => x.layerid === node.layerid);
        const opacity = defaultItem?.opacity ?? featureLayer.opacity;

        if (rootNode.opacity === undefined || defaultItem?.opacity) {
            rootNode.opacity = opacity;
        }

        if (node && node.minScale !== undefined) {
            (layer as Sublayer).minScale = node.minScale;
        }

        node.readonly = readonly;

        this.syncUpdates(["visible"], layer, node, visibilityEvent, defaults);
        this.syncUpdates(["opacity"], layer, rootNode, null, defaults);
    }

    private isLayerReadOnly(layerView: LayerView, targetLayer: Layer | Sublayer | WMSSublayer) {
        if (layerView.layer === targetLayer) {
            return false;
        }
        return this.layerService.isSubLayersReadOnly(layerView.layer);
    }

    private getLinkedLayer(layerView: LayerView, node: INode) {
        if (layerView.layer.type === LayerType.wms) {
            const allSublayers = (layerView.layer as WMSLayer).allSublayers;
            return allSublayers.find(s => s.name === node.sublayerName);
        }

        if (node.sublayerid === undefined) {
            return layerView.layer;
        }
        const allSublayers = (layerView.layer as MapImageLayer).allSublayers;

        return allSublayers.find(s => s.id === node.sublayerid);
    }

    private setupNodeEvents(node: INode, view: MapView): void {
        node.checkChanged?.pipe(skipUntil(this.nodeTreeCreated$)).pipe(takeUntil(this.unsubscribe$)).subscribe((n) => this.handleVisibleChanged(n, view));
        node.opacityChanged?.pipe(takeUntil(this.unsubscribe$)).subscribe((n) => this.handleOpacityChanged(n, view));
    }

    private handleVisibleChanged(node: INode, view: any) {
        const allChangedNodes = this.layerNodeTreeService.nodeChanged(node);

        const bound = allChangedNodes.filter(n => n.layerid);
        bound.forEach(n => {
            this.layerService.toggleLayer(n.checked, view, n.layerid, n.sublayerid, n.sublayerName);
        });
    }

    private handleOpacityChanged(node: INode, view: MapView) {
        if (node.nodeType === "root") {
            this.updateChildNodesOpacity(node);
        } else {
            this.layerService.setOpacity(node.layerid, node.opacity, view);
        }
    }

    private updateChildNodesOpacity(node: INode) {
        const bound = this.layerNodeTreeService.getLayerBoundNodes([node]).filter(n => n.id !== node.id);
        bound.forEach(n => n.opacity = node.opacity);
    }

    private syncUpdates(properties: string[], layer: Layer | Sublayer | WMSSublayer, node: INode, visibilityEvent: EventEmitter<LayerVisibility>, defaults: IDefaults) {
        properties.forEach(property => {
            reactiveUtils.watch(
                () => (layer[property]),
                (newVal) => {
                    const oldVal = node[property];
                    if (newVal === oldVal) {
                        return;
                    }
                    node[property] = newVal;
                    if (property === "visible") {
                        // state change kan komma från lager som ej administreras av lagerväljaren
                        // då saknas webinställningar för dem. (t ex collector-lagret)
                        if (ObjectUtils.hasValue(defaults)) {
                            const def = defaults.defaults?.find(x => x.layerid === node.layerid &&
                                ((ObjectUtils.hasValue(node.sublayerName) &&  x.sublayerName === node.sublayerName) || x.sublayerid === node.sublayerid));
                            if (def) {
                                def.visible = newVal;
                            } else {
                                defaults.defaults.push({
                                    layerid: node.layerid,
                                    sublayerid: node.sublayerid,
                                    sublayerName: node.sublayerName,
                                    visible: newVal
                                } as IDefault);
                            }
                        }
                        visibilityEvent?.emit(new LayerVisibility(node.layerid, node.sublayerid, node.sublayerName, newVal));
                    }
                });
        });
    }
}
